/**
 * LayoutPickerModal — modal for choosing output format before generation.
 */

import React, { useState, useEffect } from "react";
import { View, Text, Pressable, Modal, Image, StyleSheet } from "react-native";
import { colors, textStyle } from "../../styles";
import { useConfigStore } from "../../store/configStore";
import comicBookImg from "../../assets/layouts/comic-book.png";
import storyBookImg from "../../assets/layouts/story-book.png";
import pictureBookImg from "../../assets/layouts/picture-book.png";
import generativeAiIcon from "../../assets/icons/generative-ai.png";

const LAYOUTS = [
  { value: "comic_book", label: "Comic Book", image: comicBookImg },
  { value: "storybook", label: "Storybook", image: storyBookImg },
  { value: "picture_book", label: "Picture Book", image: pictureBookImg },
] as const;

interface Props {
  visible: boolean;
  onClose: () => void;
  onGenerate: () => void;
}

export default function LayoutPickerModal({ visible, onClose, onGenerate }: Props) {
  const { outputFormat, setOutputFormat } = useConfigStore();
  const [selected, setSelected] = useState(outputFormat);

  useEffect(() => {
    if (visible) setSelected(outputFormat);
  }, [visible, outputFormat]);

  const handleGenerate = () => {
    setOutputFormat(selected);
    onGenerate();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.centerer}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <View style={styles.modal}>
          <Text style={styles.title}>Choose a layout</Text>

          <View style={styles.options}>
            {LAYOUTS.map((layout) => {
              const isSelected = selected === layout.value;
              return (
                <Pressable key={layout.value} style={[styles.option, !isSelected && styles.optionDimmed]} onPress={() => setSelected(layout.value)}>
                  <View style={[styles.preview, isSelected && styles.previewSelected]}>
                    <Image source={layout.image} style={styles.previewImage} resizeMode="cover" />
                  </View>
                  <Text style={[styles.label, isSelected && styles.labelSelected]}>{layout.label}</Text>
              </Pressable>
            );
            })}
          </View>

          <Pressable style={styles.generateBtn} onPress={handleGenerate}>
            <Image source={generativeAiIcon} style={styles.generateIcon} resizeMode="contain" />
            <Text style={styles.generateBtnText}>Generate</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.6)",
  },
  centerer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    backgroundColor: colors.galleryBg,
    borderRadius: 36,
    paddingHorizontal: 72,
    paddingTop: 64,
    paddingBottom: 32,
    alignItems: "center",
    maxWidth: 1060,
    width: "90%",
  },
  title: {
    ...textStyle.headingMedium,
    fontSize: 40,
    color: colors.bg,
    marginBottom: 24,
  },
  options: {
    flexDirection: "row",
    gap: 20,
    marginBottom: 40,
  },
  option: {
    flex: 1,
    alignItems: "center",
    gap: 24,
  },
  optionDimmed: {
    opacity: 0.65,
  },
  preview: {
    width: "100%",
    aspectRatio: 362 / 203.5,
    borderRadius: 12,
    overflow: "hidden",
    borderWidth: 2,
    borderColor: colors.borderBeat,
  },
  previewSelected: {
    borderColor: colors.borderFocus,
    borderWidth: 10,
  },
  previewImage: {
    width: "100%",
    height: "100%",
  },
  label: {
    ...textStyle.headingMedium,
    fontSize: 24,
    color: colors.white,
    textAlign: "center",
  },
  labelSelected: {
    color: colors.labelSelected,
  },
  generateBtn: {
    backgroundColor: colors.galleryCloseBtnBg,
    borderRadius: 32,
    paddingVertical: 20,
    paddingHorizontal: 20,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  generateIcon: {
    width: 24,
    height: 24,
  },
  generateBtnText: {
    ...textStyle.heading,
    fontSize: 24,
    color: colors.primary,
  },
});
